import { ButtonGroup, type ButtonGroupItem } from '@trustoo/components/ButtonGroup/v2'
import React, { useState } from 'react'
import { CodeSnippet, InlineCode, MIcon, Section } from './shared'

const BUTTON_GROUP_USAGE = `import { ButtonGroup } from '@trustoo/components/ButtonGroup/v2'

const [view, setView] = useState('week')

<ButtonGroup
    aria-label="View"
    value={view}
    onChange={setView}
    items={[
        { id: 'day', label: 'Day' },
        { id: 'week', label: 'Week' },
        { id: 'month', label: 'Month' },
    ]}
/>

// Per-item icon options (one Icon axis member per item)
{ id: 'list', label: 'List', iconLeading: <ListIcon /> }   // Icon=Leading
{ id: 'left', label: 'Align left', iconLeading: <AlignLeftIcon />, iconOnly: true } // Icon=Only
{ id: 'archived', label: 'Archived', disabled: true }      // per-item disabled`

const StatefulButtonGroup: React.FC<{
    label: string
    ariaLabel: string
    initial: string
    items: ButtonGroupItem[]
}> = ({ label, ariaLabel, initial, items }) => {
    const [value, setValue] = useState(initial)
    return (
        <div className="space-y-spacing-md">
            <span className="text-sm font-semibold text-text-primary">{label}</span>
            <div>
                <ButtonGroup
                    aria-label={ariaLabel}
                    value={value}
                    onChange={setValue}
                    items={items}
                />
            </div>
        </div>
    )
}

export const ButtonGroupShowcase: React.FC = () => (
    <Section
        title="Button group (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Button group</span> set
                (ComponentSet <InlineCode>5:3893</InlineCode>). A single-select segmented control:
                pass an <InlineCode>items</InlineCode> array plus a controlled{' '}
                <InlineCode>value</InlineCode> / <InlineCode>onChange</InlineCode>. Import v2
                explicitly via <InlineCode>@trustoo/components/ButtonGroup/v2</InlineCode> (net-new
                component — there is no v1 barrel).
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="grid gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl sm:grid-cols-2">
                <StatefulButtonGroup
                    label="Text"
                    ariaLabel="Date range"
                    initial="week"
                    items={[
                        { id: 'day', label: 'Day' },
                        { id: 'week', label: 'Week' },
                        { id: 'month', label: 'Month' },
                    ]}
                />
                <StatefulButtonGroup
                    label="Leading icon"
                    ariaLabel="Layout"
                    initial="board"
                    items={[
                        {
                            id: 'list',
                            label: 'List',
                            iconLeading: (
                                <MIcon name="format_list_bulleted" className="text-[20px]" />
                            ),
                        },
                        {
                            id: 'board',
                            label: 'Board',
                            iconLeading: <MIcon name="grid_view" className="text-[20px]" />,
                        },
                        {
                            id: 'calendar',
                            label: 'Calendar',
                            iconLeading: <MIcon name="calendar_today" className="text-[20px]" />,
                        },
                    ]}
                />
                <StatefulButtonGroup
                    label="Icon only"
                    ariaLabel="Text alignment"
                    initial="center"
                    items={[
                        {
                            id: 'left',
                            label: 'Align left',
                            iconLeading: <MIcon name="format_align_left" className="text-[20px]" />,
                            iconOnly: true,
                        },
                        {
                            id: 'center',
                            label: 'Align center',
                            iconLeading: (
                                <MIcon name="format_align_center" className="text-[20px]" />
                            ),
                            iconOnly: true,
                        },
                        {
                            id: 'right',
                            label: 'Align right',
                            iconLeading: (
                                <MIcon name="format_align_right" className="text-[20px]" />
                            ),
                            iconOnly: true,
                        },
                    ]}
                />
                <StatefulButtonGroup
                    label="With a disabled item"
                    ariaLabel="Status"
                    initial="active"
                    items={[
                        { id: 'active', label: 'Active' },
                        { id: 'paused', label: 'Paused' },
                        { id: 'archived', label: 'Archived', disabled: true },
                    ]}
                />
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={BUTTON_GROUP_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Controlled single-select: the item whose <InlineCode>id</InlineCode> equals{' '}
                    <InlineCode>value</InlineCode> renders selected (
                    <InlineCode>bg-background-bg-primary_hover</InlineCode>). Each{' '}
                    <InlineCode>ButtonGroupItem</InlineCode> takes a <InlineCode>label</InlineCode>{' '}
                    and an optional <InlineCode>iconLeading</InlineCode> (set{' '}
                    <InlineCode>iconOnly</InlineCode> to drop the label) plus{' '}
                    <InlineCode>disabled</InlineCode>. Items render as{' '}
                    <InlineCode>&lt;button&gt;</InlineCode>s with{' '}
                    <InlineCode>aria-pressed</InlineCode>; pass <InlineCode>aria-label</InlineCode>{' '}
                    to label the group.
                </p>
            </div>
        </div>
    </Section>
)
